import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, signal, TemplateRef, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Router, RouterModule } from '@angular/router';
import { Store } from '@ngxs/store';
import { TuiDataListModule, TuiHostedDropdownModule } from '@taiga-ui/core';
import { TuiAvatarModule } from '@taiga-ui/kit';
import { firstValueFrom, map } from 'rxjs';
import { environment } from '@env';
import { AuthActions } from '@core/store/auth/auth.actions';
import { EmailVerificationService } from '@core/services/email-verification.service';
import { ProfileState } from '@core/store/profile/profile.state';
import { NotificationBellComponent } from '@shared/components/notification-bell/notification-bell.component';
import { FullscreenLayoutService } from '@layout/fullscreen-layout.service';
import { ReportsVisitService } from '@modules/reportes/reports-visit';

const BANNER_SESSION_KEY = 'email-verification-banner-dismissed';

function readDismissedFlag(): boolean {
  try {
    return sessionStorage.getItem(BANNER_SESSION_KEY) === '1';
  } catch {
    // sessionStorage unavailable (private-mode iOS, etc.) — default to not-dismissed
    return false;
  }
}

@Component({
  selector: 'rec-skeleton',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    RouterModule,
    NotificationBellComponent,
    TuiAvatarModule,
    TuiHostedDropdownModule,
    TuiDataListModule,
  ],
  templateUrl: './skeleton.component.html',
  styleUrl: './skeleton.component.scss',
})
export class SkeletonComponent {
  private readonly afAuth = inject(AngularFireAuth);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly emailVerificationService = inject(EmailVerificationService);
  private readonly fullscreenLayout = inject(FullscreenLayoutService);

  readonly isFullScreen = this.fullscreenLayout.active;

  /**
   * D-8 (REQ-RSENT-060) — mientras la pantalla obligatoria `/reclutador/plan` está montada, el
   * shell oculta los escapes de navegación (logo, "Créditos", campana) que el `subscriptionGuard`
   * rebotaría de vuelta a `/plan` (bucle percibido, R6). El logout NO se suprime — es la única
   * salida deliberada.
   */
  readonly navSuppressed = this.fullscreenLayout.navSuppressed;

  /** The "Reportes" menu entry exists only while the reports flag is on. */
  readonly reportsEnabled = environment.reports;

  /** «Nuevo» rides on "Reportes" until the recruiter opens the reports in this browser. */
  readonly reportsVisited = inject(ReportsVisitService).visited;

  // Expuesto para tests: `tui-hosted-dropdown` monta el `<ng-template #userMenu>`
  // en un overlay CDK que jsdom no renderiza. Con esta referencia, el spec
  // puede crear el embedded view directamente y testear el `(click)="signOut()"`
  // del template en lugar de invocar `signOut()` por método (que pasaría verde
  // aunque el binding del template estuviera roto).
  @ViewChild('userMenu', { read: TemplateRef })
  userMenuTemplate?: TemplateRef<unknown>;

  /**
   * Sesión activa: gobierna visibilidad del avatar y la campana de notificaciones. Una identidad
   * anónima no es una cuenta: sólo existe para el sandbox de screening de CVs, y cerrar sesión le
   * haría perder su sandbox (J5).
   */
  readonly isAuthenticated = toSignal(
    this.afAuth.authState.pipe(map((u) => !!u && !u.isAnonymous)),
    { initialValue: false }
  );

  /** Selector signal: true when email is verified (or null — grandfathering) */
  readonly emailVerified = this.store.selectSignal(ProfileState.emailVerified);

  /** Perfil del usuario autenticado (fuente para nombre / inicial del avatar). */
  readonly profile = this.store.selectSignal(ProfileState.profile);

  /** Inicial mostrada en el avatar — usa name → username → email como fuente. */
  readonly userInitial = computed(() => {
    const profile = this.profile();
    const source =
      profile?.name?.trim() || profile?.username?.trim() || profile?.email?.trim() || '';
    return source ? source.charAt(0).toUpperCase() : '?';
  });

  /** Whether the banner is dismissed for this browser session. */
  readonly bannerDismissed = signal(readDismissedFlag());

  /** Feedback message after resend (ephemeral) */
  readonly resendFeedback = signal<string | null>(null);

  /** Show banner only if unverified AND not dismissed (#15) */
  readonly showBanner = computed(() => !this.emailVerified() && !this.bannerDismissed());

  dismissBanner(): void {
    try {
      sessionStorage.setItem(BANNER_SESSION_KEY, '1');
    } catch {
      // ignore in private-mode iOS
    }
    this.bannerDismissed.set(true);
  }

  resendVerification(): void {
    this.resendFeedback.set(null);
    this.emailVerificationService.resend().subscribe((message) => {
      this.resendFeedback.set(message);
    });
  }

  async signOut(): Promise<void> {
    await firstValueFrom(this.store.dispatch(new AuthActions.SignOut()));
    await this.router.navigate(['/reclutador/login']);
  }
}
